import { useMemo, useEffect } from "react";
import { pushGtmEvent } from "@/lib/analytics";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Seo } from "@/components/seo/Seo";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import BulkImportWizard from "@/components/b2b/BulkImportWizard";

const schedulerSignals = [
  "checkout, check-in e finestra utile",
  "durata stimata dell'intervento",
  "fornitore scelto o copertura da definire",
  "capacita del vendor e stato del batch",
  "carico reale del fornitore (Tracker: piattaforma + prenotazioni esterne)",
];

const schedulerOutputs = [
  "batch coperti, da confermare o a rischio",
  "priorita operative sui turnover piu stretti",
  "suggerimenti di pianificazione e conferma",
  "segnali di over-capacity per le giornate piu cariche",
];

export default function BulkBookingImport() {
  const params = useParams() as { companyId?: string; serviceId?: string };
  const navigate = useNavigate();
  const title = useMemo(() => "Bulk Order (beta) | Halomora", []);

  useEffect(() => {
    pushGtmEvent("start_booking", {
      booking_flow: "bulk",
      company_id: params.companyId,
      service_id: params.serviceId,
    });
  }, [params.companyId, params.serviceId]);

  return (
    <div className="min-h-screen app-page">
      <Seo title={title} noindex canonicalPath="/bulk-order" />
      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <Card className="border-gray-200 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 break-words">
                Bulk Order <Badge variant="secondary">beta</Badge>
              </CardTitle>
              <CardDescription className="break-words">
                Per apartment manager: carica fino a 30 ordini in un colpo. La pulizia verra pianificata dal fornitore
                tra checkout e check-in. Il Bulk Order e' il canale di ingresso dei tuoi turnover dentro Halomora: il
                fornitore poi li gestisce nel Tracker booking insieme alle altre prenotazioni e l&apos;AI Turnover
                Scheduler calcola priorita, copertura e rischio operativo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BulkImportWizard initialCompanyId={params.companyId} initialServiceId={params.serviceId} />
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
            <CardHeader>
              <CardTitle>Perche questo flusso e' il wedge giusto</CardTitle>
              <CardDescription>
                Il problema reale non e' solo importare i turnover, ma capire rapidamente quali sono coperti, quali
                rischiano di uscire finestra e dove serve intervenire prima.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <div className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">Input che il sistema ha gia</div>
                {schedulerSignals.map((item) => (
                  <div key={item} className="rounded-xl border border-primary/10 bg-white/80 px-4 py-3 text-sm text-gray-700">
                    {item}
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <div className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">Output del primo scheduler</div>
                {schedulerOutputs.map((item) => (
                  <div key={item} className="rounded-xl border border-primary/10 bg-white/80 px-4 py-3 text-sm text-gray-700">
                    {item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
            <CardHeader>
              <CardTitle>Non hai ancora il partner pulizie?</CardTitle>
              <CardDescription>
                Se il tuo problema non è caricare i turnover ma trovare una copertura continuativa per la stagione, abbiamo creato una landing dedicata per apartment manager.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate("/apartment-manager/partner-pulizie")}>
                  Richiedi supporto commerciale
                </Button>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Torna alla Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

